import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["crmObject", "nonCrmObject"];
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ApolloProvider } from '@apollo/client';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { LayoutWithCollaborationSidebar } from 'collaboration-sidebar';
import { ContentApprovalsProvider, ContentObjectTypes } from 'content-management-data-lib';
import ContentDetailsDataProvider from 'content-management-details-lib/providers/ContentDetailsDataProvider';
import { DataFetchingClient, DataFetchingClientProvider } from 'data-fetching-client';
import { createApolloClient } from 'hubspot-apollo-client';
import { ApprovalTypes } from 'ui-product-approvals/types';
import PagesDetailsPage from '../components/PagesDetailsPage';
import { ContentTypeCollabSidebarAppName, ENABLED_COLLABORATION_FEATURES } from '../constants/collaborationSidebar';
import { ALL_METRICS, DEFAULT_METRICS_TO_DISPLAY } from '../constants/reporting';
import usePageContentObjectType from '../hooks/usePageContentObjectType';
import useSitePageActionsConfigOptions from '../hooks/useSitePagesActionsConfig';
import useLandingPageActionsConfigOptions from '../hooks/useLandingPageActionsConfig';
import useRouteParams from '../hooks/useRouteParams';
import { selectAuthState } from '../selectors';
import { usageTracker } from '../utils/usageTracker';
import { DETAILS_HEADER_CONFIG_OVERRIDES } from '../constants/detailsRenderConfig';
import DowngradeAlerts from '../components/DowngradeAlerts';
import { LANDING_PAGES_LIMIT_NAME_TO_LIMIT_TYPE_MAP, SITE_PAGES_LIMIT_NAME_TO_LIMIT_TYPE_MAP } from 'page-lib/constants/limits';
import useContentId from '../hooks/useContentId';
import useSmartContent from '../hooks/useSmartContent';
import RemoveBrandingBanner from '../components/RemoveBrandingBanner';
import { useGetDetailsPanelConfigOverrides } from '../hooks/useGetDetailsPanelConfigOverrides';
import useTitleConfigOverride from '../hooks/useTitleConfigOverride';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const LIMIT_NAME_TO_LIMIT_TYPE_MAPS = {
  [ContentObjectTypes.LANDING_PAGE]: LANDING_PAGES_LIMIT_NAME_TO_LIMIT_TYPE_MAP,
  [ContentObjectTypes.SITE_PAGE]: SITE_PAGES_LIMIT_NAME_TO_LIMIT_TYPE_MAP
};
function DFCProvider({
  children
}) {
  const client = useMemo(() => {
    return new DataFetchingClient();
  }, []);
  const apolloClient = useMemo(() => createApolloClient(), []);
  return /*#__PURE__*/_jsx(ApolloProvider, {
    client: apolloClient,
    children: /*#__PURE__*/_jsx(DataFetchingClientProvider, {
      client: client,
      children: children
    })
  });
}
const PagesDetailsContainer = () => {
  const contentId = useContentId();
  const {
    pageTypeSlug
  } = useRouteParams();
  const userInfo = useSelector(selectAuthState);
  const contentObjectType = usePageContentObjectType();
  const detailsPanelConfigOverrides = useGetDetailsPanelConfigOverrides();
  const titleConfigOverride = useTitleConfigOverride();

  // Kicks off the smart content fetch early
  useSmartContent();
  const approvalType = contentObjectType === ContentObjectTypes.SITE_PAGE ? ApprovalTypes.SITE_PAGE : ApprovalTypes.LANDING_PAGE;
  const useActionsConfig = contentObjectType === ContentObjectTypes.SITE_PAGE ? useSitePageActionsConfigOptions : useLandingPageActionsConfigOptions;
  const trackerProps = useMemo(() => {
    const onTrackEvent = (action, eventProps) => {
      const actionEventProps = _objectWithoutPropertiesLoose(eventProps, _excluded);
      usageTracker.track('actionsInteraction', Object.assign({}, actionEventProps, {
        action
      }));
    };
    return {
      applicationId: 'page-details-ui',
      tracker: usageTracker,
      onTrackEvent
    };
  }, []);
  if (!userInfo.user) {
    return /*#__PURE__*/_jsx(LoadingSpinner, {});
  }
  return /*#__PURE__*/_jsx(LayoutWithCollaborationSidebar, {
    appName: ContentTypeCollabSidebarAppName[pageTypeSlug],
    enabledFeatures: ENABLED_COLLABORATION_FEATURES,
    objectId: contentId,
    children: /*#__PURE__*/_jsx(ContentApprovalsProvider, {
      approvalType: approvalType,
      hasApprovalsReadAccess: true,
      children: /*#__PURE__*/_jsxs(ContentDetailsDataProvider, {
        contentId: contentId,
        contentObjectType: contentObjectType,
        allMetricsData: ALL_METRICS,
        defaultMetric: "rawViews",
        trackerProps: trackerProps,
        userInfo: userInfo,
        defaultMetricsToDisplay: DEFAULT_METRICS_TO_DISPLAY,
        limitNameToLimitTypeMap: LIMIT_NAME_TO_LIMIT_TYPE_MAPS[contentObjectType],
        detailsPanelConfigOverrides: detailsPanelConfigOverrides,
        detailsHeaderConfigOverrides: DETAILS_HEADER_CONFIG_OVERRIDES,
        titleConfigOverride: titleConfigOverride
        // TODO: only fetch usage limits if portal is in a lower tier
        ,
        shouldFetchUsageLimits: true,
        contentTypeHasLegacyAnalyticsPages: true,
        children: [/*#__PURE__*/_jsx(RemoveBrandingBanner, {
          contentObjectType: contentObjectType
        }), /*#__PURE__*/_jsx(DowngradeAlerts, {}), /*#__PURE__*/_jsx(PagesDetailsPage, {
          useActionsConfig: useActionsConfig
        })]
      })
    })
  });
};
const PagesDetailsContainerWithDFCProvider = () => {
  return /*#__PURE__*/_jsx(DFCProvider, {
    children: /*#__PURE__*/_jsx(PagesDetailsContainer, {})
  });
};
export default PagesDetailsContainerWithDFCProvider;